import { useParams, Link } from 'react-router-dom';
import ArticleNavbar from './components/ArticleNavbar';
import ArticleFooter from './components/ArticleFooter';
import { getArticleBySlug } from '@/mocks/articles';
import type { ArticleSection, ArticleFAQ, ArticleLink } from '@/mocks/articles';
import categories from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

function renderSection(section: ArticleSection, idx: number) {
  switch (section.type) {
    case 'h2':
      return (
        <h2 key={idx} className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mt-12 mb-5">
          {section.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={idx} className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-4">
          {section.text}
        </h3>
      );
    case 'p':
      return (
        <p key={idx} className="text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.text || '' }}></p>
      );
    case 'ol':
      return (
        <ol key={idx} className="space-y-3 list-decimal pl-5">
          {(section.items || []).map((item, i) => (
            <li key={i} className="text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item }}></li>
          ))}
        </ol>
      );
    case 'ul':
      return (
        <ul key={idx} className="space-y-3 list-disc pl-5">
          {(section.items || []).map((item, i) => (
            <li key={i} className="text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item }}></li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div key={idx} className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-background-100">
                {(section.headers || []).map((h, i) => (
                  <th key={i} className="text-left p-3 font-semibold text-foreground-900 border-b border-background-200 first:text-left text-center">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(section.rows || []).map((row, ri) => (
                <tr key={ri} className="border-b border-background-100">
                  {row.map((cell, ci) => (
                    <td key={ci} className="p-3 first:font-medium text-center first:text-left" dangerouslySetInnerHTML={{ __html: cell }}></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'tip':
      return (
        <div key={idx} className="bg-primary-50 border border-primary-200 rounded-2xl p-6 my-8">
          <p className="text-sm text-primary-800 font-medium" dangerouslySetInnerHTML={{ __html: `<i class="ri-lightbulb-line mr-2"></i>${section.text || ''}` }}></p>
        </div>
      );
    case 'faq':
      return (
        <div key={idx} className="space-y-4">
          {(section.faqs || []).map((faq: ArticleFAQ, fi: number) => (
            <div key={fi} className="bg-background-50 border border-background-200 rounded-xl p-5">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{faq.question}</h3>
              <p className="text-sm text-foreground-600" dangerouslySetInnerHTML={{ __html: faq.answer }}></p>
            </div>
          ))}
        </div>
      );
    case 'links':
      return (
        <div key={idx} className="bg-background-100 border border-background-200 rounded-2xl p-6 my-10">
          <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">Przydatne linki</h4>
          <ul className="space-y-2">
            {(section.links || []).map((link: ArticleLink, li: number) => (
              <li key={li}>
                {link.external ? (
                  <a href={link.url} target="_blank" rel="noopener nofollow" className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1.5">
                    <i className={`${link.icon} text-xs`}></i>
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.url} className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1.5">
                    <i className={`${link.icon} text-xs`}></i>
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    case 'category-grid':
      return (
        <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 p-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer group"
            >
              <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg group-hover:bg-primary-500 group-hover:text-background-50 transition-all flex-shrink-0">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors whitespace-nowrap">{cat.name}</span>
              <i className="ri-arrow-right-line text-foreground-300 ml-auto text-xs"></i>
            </a>
          ))}
        </div>
      );
    case 'cta':
      return (
        <div key={idx} className="mt-12 bg-primary-500 text-background-50 rounded-2xl p-8 text-center">
          <h3 className="font-heading text-2xl font-semibold mb-3">{section.ctaTitle}</h3>
          <p className="text-primary-100 text-sm mb-6 max-w-md mx-auto">{section.ctaDesc}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {(section.ctaButtons || []).map((btn, bi) => (
              btn.external ? (
                <a
                  key={bi}
                  href={btn.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className={`w-full sm:w-auto px-8 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 ${bi === 0 ? 'bg-background-50 text-primary-600 hover:bg-background-100' : 'bg-primary-600 text-background-50 hover:bg-primary-700 border border-primary-400'}`}
                >
                  <i className={btn.icon}></i>
                  {btn.label}
                </a>
              ) : (
                <Link
                  key={bi}
                  to={btn.url}
                  className={`w-full sm:w-auto px-8 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 ${bi === 0 ? 'bg-background-50 text-primary-600 hover:bg-background-100' : 'bg-primary-600 text-background-50 hover:bg-primary-700 border border-primary-400'}`}
                >
                  <i className={btn.icon}></i>
                  {btn.label}
                </Link>
              )
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) return <NotFound />;

  const article = getArticleBySlug(slug);

  if (!article) return <NotFound />;

  return (
    <div className="min-h-screen bg-background-50">
      <ArticleNavbar />

      <main className="pt-16">
        {article.schema?.faqs && (
          <>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  "headline": article.title,
                  "description": article.intro.replace(/<\/?[^>]+(>|$)/g, '').substring(0, 160),
                  "image": article.featuredImage.src,
                  "datePublished": "2026-07-01",
                  "dateModified": "2026-07-07",
                  "author": {
                    "@type": "Organization",
                    "name": "Kakobuy Finds",
                    "url": import.meta.env.VITE_SITE_URL
                  },
                  "publisher": {
                    "@type": "Organization",
                    "name": "Kakobuy Finds",
                    "logo": {
                      "@type": "ImageObject",
                      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png"
                    }
                  },
                  "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": import.meta.env.VITE_SITE_URL + "/artykul/" + article.slug
                  },
                  "inLanguage": "pl"
                }),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": (article.schema.faqs || []).map((faq) => ({
                    "@type": "Question",
                    "name": faq.question,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": faq.answer.replace(/<\/?[^>]+(>|$)/g, ''),
                    },
                  })),
                }),
              }}
            />
          </>
        )}

        <article className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <nav className="mb-8 text-xs text-foreground-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors">Strona Główna</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">{article.title}</span>
          </nav>

          <header className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Aktualizacja: {article.meta.updated}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                Czas czytania: {article.meta.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-file-text-line"></i>
                {article.meta.words}
              </span>
            </div>
          </header>

          <div className="w-full aspect-[21/9] rounded-2xl overflow-hidden mb-10 bg-background-200">
            <img
              src={article.featuredImage.src}
              alt={article.featuredImage.alt}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            <p className="text-lg text-foreground-800 font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: article.intro }}></p>
            {article.sections.map((section, idx) => renderSection(section, idx))}
          </div>

          <div className="mt-12 pt-10 border-t border-background-200">
            <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-5">
              Powiązane artykuły
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(article.relatedArticles || []).map((ra, i) => (
                ra.external ? (
                  <a
                    key={i}
                    href={ra.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block p-5 bg-background-50 border border-background-200 rounded-xl hover:border-primary-300 transition-colors group"
                  >
                    <h4 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{ra.desc}</p>
                  </a>
                ) : (
                  <Link
                    key={i}
                    to={ra.url}
                    className="block p-5 bg-background-50 border border-background-200 rounded-xl hover:border-primary-300 transition-colors group"
                  >
                    <h4 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{ra.desc}</p>
                  </Link>
                )
              ))}
            </div>
          </div>
        </article>
      </main>

      <ArticleFooter />
    </div>
  );
}